import { MutableRefObject } from 'react';
import { redirect } from '../../providers/MitekProvider/MitekUtils';
import { TNaturalDisaster } from '../../providers/Pushdown/PushdownProvider';
import { ApplicationApprovedResponse } from '../../types/responses/ApplicationApprovedResponse';
import { ApplicationPendingResponse } from '../../types/responses/ApplicationPendingResponse';
import { GetApplicationAuthorizedResponse } from '../../types/responses/GetApplicationAuthorizedResponse';
import { RefreshCurrentApplicationResponse } from '../../types/responses/RefreshCurrentApplicationResponse';
import { SendSMSCustomerVerificationLinkResponse } from '../../types/responses/SendSMSCustomerVerificationLinkResponse';
import { acquisitionApiRequest } from '../../utils/acquisition-api/AcquisitionApi';
import { AcquisitionFlow, clearSessionAndRedirectToDataEntryPage, getAcquisitionFlow, getAppData } from '../../utils/acquisition-utils';
import SessionStorage from '../../utils/SessionStorage';
import { IMitekDesktopUpdaterProps } from './MitekDesktopUpdater';

export const callResendSMS = (props: IMitekDesktopUpdaterProps, showPushdown: (arg: string | TNaturalDisaster, codeOnly?: boolean) => void) => async () => 
    await acquisitionApiRequest<SendSMSCustomerVerificationLinkResponse>('send-sms-customer-verification-link', {})
        .then((response) => {
            if (response.data && 'IsError' in response.data && response.data.IsError) {
                showPushdown({
                    header: 'There was an error',
                    message: [response.data.ErrorMessage],
                    description: '',
                    color: 'error',
                });
                console.error(response.data.ErrorMessage);
                return false;
            }
            return true;
        })
        .catch((rejection) => {
            console.error(rejection);
            showPushdown(props.apiErrorCode!);
        });

export const applicationPending = (props: IMitekDesktopUpdaterProps, errorPushdownAPI: (reason: any) => void) => async () => {
    const pendingApiNames: Record<AcquisitionFlow, string> = {
        preQual: 'pq-app-pending',
        preApproved: 'pa-app-pending',
        skipPQ: 'skip-pq-app-pending',
        pqOne: 'skip-pq-app-pending', 
        partnerHosted: '', 
    };
    await acquisitionApiRequest<ApplicationPendingResponse>(pendingApiNames[getAcquisitionFlow()], {} )
        .then(async (response) => {
            await SessionStorage.setCipherWithCurrentScope(
                'app-pending-data',
                response.data,
            );
            redirect(props.pendingApprovalPageUrl!);
        })
        .catch(errorPushdownAPI);
};

export const applicationAuthorized = (props: IMitekDesktopUpdaterProps, errorPushdownAPI: (reason: any) => void) => async () => {
    await acquisitionApiRequest<GetApplicationAuthorizedResponse>('get-application-authorized', {})
        .then((response) => {
            SessionStorage.setObjectWithCurrentScope('appAuthorizedResponse', response.data);
            switch (parseInt(response.data.NextAction)) {
                case 0:
                    clearSessionAndRedirectToDataEntryPage('appInProgress', props.dataEntryUrl);
                    break;
                case 1: // KIQ/Level Three Questions;
                    redirect(props.levelThreePageUrl!);
                    break;
                case 2: // One-Time Passcode (OTP)
                    redirect(props.oneTimePasscodeUrl!);
                    break;
                default:
                    SessionStorage.remove('appAuthorizedResponse');
                    clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryUrl, 'Default application authorization error');
            }
        }).catch(errorPushdownAPI);
};

export const applicationApproved = (props: IMitekDesktopUpdaterProps, errorPushdownAPI: (reason: any) => void) => async () => {
    const approvedApiNames: Record<AcquisitionFlow, string> = {
        preQual: 'pq-app-approved',
        preApproved: 'pa-app-approved',
        skipPQ: 'skip-pq-app-approved',
        pqOne: 'skip-pq-app-approved',
        partnerHosted: '',
    };
    await acquisitionApiRequest<ApplicationApprovedResponse>(approvedApiNames[getAcquisitionFlow()], {})
        .then(async (response) => {
            await SessionStorage.setCipherWithCurrentScope('app-approved-data', response.data);
            const appData = await getAppData();
            const isSecuredCard = appData.IsSecuredCard;
            const fullAppResponse = await SessionStorage.getCipher('fullAppResponse', true);
            const isRiskBasedPricingNoticeRequired = (fullAppResponse && 'isRiskBasedPricingNoticeRequired' in fullAppResponse ? fullAppResponse.isRiskBasedPricingNoticeRequired : false);
            const isCounterOffer = SessionStorage.get('isCounterOffer') === 'true';

            switch (response.data.InstantIssuanceOffer) {
                case 1: // Instant Issuance Approved
                case 0: { // None i.e. no special offer
                    if (isSecuredCard) {
                        redirect(props.pendingFundingUrl);
                    } else if (isRiskBasedPricingNoticeRequired && !isCounterOffer) {
                        redirect(props.riskBasedEsignUrl!);
                    } else {
                        redirect(props.confirmationPageUrl!);
                    }
                    break;
                }  
                case 2: { // Mobile Wallet Approved
                    const isSignedEsign = SessionStorage.verifyNotEmpty('isEsign') && SessionStorage.getObject('isEsign');
                    const isSignedLimitedEsign = SessionStorage.verifyNotEmpty('visitedEsign') && SessionStorage.getObject('visitedEsign');

                    SessionStorage.set('rbPricingRequired', isRiskBasedPricingNoticeRequired);
                    const redirectUrl = isRiskBasedPricingNoticeRequired && !isSignedEsign && !isSignedLimitedEsign? props.riskBasedEsignUrl : props.mobileWalletUrl;
                    redirect(redirectUrl);
                    break;
                }
                default:
                    SessionStorage.remove('app-approved-data');
                    clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryUrl, 'Default instant issuance offer error');
            }
        }).catch(errorPushdownAPI);
};

export const RefreshCurrentApplication = (
    props: IMitekDesktopUpdaterProps,  
    errorPushdownAPI: (reason: any) => void, 
    intervalRef: MutableRefObject<NodeJS.Timeout>) => async () => {
    await acquisitionApiRequest<RefreshCurrentApplicationResponse>('refresh-current-application', {})
        .then((response) => {
            if (response.status !== 200) {
                throw response;
            }
            switch (response.data.SubmitApplicationEnum) {
                case 0: // VerificationFailed
                    clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryUrl, 'Verification failed');
                    break;
                case 1: // DeclinedWithEsignCredit
                case 2: // DeclinedWithEsignCreditNoScore
                case 3: // DeclinedWithEsignNonCredit
                case 4: // DeclinedWithEsignNCDLetter
                    SessionStorage.remove('isCounterOffer');
                    redirect(props.limitedEsignUrl!);
                    break;
                case 7: // Approved
                    clearInterval(intervalRef.current);
                    applicationApproved(props, errorPushdownAPI)();
                    break;
                case 8: // Pending
                    clearInterval(intervalRef.current);
                    applicationPending(props, errorPushdownAPI)();
                    break;
                case 9: // Authorise
                    // Intended as a "Do Nothing" as the authorize response itself implies we should stay in Mitek.
                    // This is a workaround to get Mitek working with the old API.
                    break;
                case 11: // (Pre-approved) Error
                    clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryUrl, 'Pre-approved error');
                    break;
                default: // null
                    clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryUrl, 'Unsupported submit application enum');
            }
        }).catch((rejection) => {
            errorPushdownAPI(rejection); 
        });
};